import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { compact, xor } from 'lodash-es';
import { DataTable } from '@ui';
import { SearchInput } from '@ds';
import { SResource, Region } from '@/api/apis/resources/schedule';
import { getFullName } from '@/utils/helpers';
import { SURGEON_COLUMNS, getRenderSurgeonTableCell } from './config';

type InsertSurgeonToRegionListProps = {
  regionId: string;
  resourceList: SResource[];
  regionsDictionary: Record<string, Region>;
  insertedIds: string[];
  selectedIds: string[];
  onChange: (userIds: string[]) => void;
};

const noopFn = () => {};

export const InsertSurgeonToRegionList = ({
  regionId,
  resourceList,
  regionsDictionary,
  insertedIds,
  selectedIds,
  onChange,
}: InsertSurgeonToRegionListProps) => {
  const [searchValue, setSearchValue] = useState('');

  const dataList = useMemo(() => {
    const list = compact(resourceList).map((r) => {
      return { ...r, isSelected: selectedIds.includes(r.id), regionName: regionsDictionary[r.regionId]?.name };
    });

    return searchValue
      ? list.filter((r) => {
          const fullName = getFullName(r.firstName, r.lastName);
          const hasName = fullName.toLowerCase().includes(searchValue.toLowerCase());
          const hasHubName = r.regionName?.toLowerCase().includes(searchValue.toLowerCase());

          return hasName || hasHubName;
        })
      : list;
  }, [searchValue, resourceList, regionsDictionary, selectedIds]);

  const handleSelect = useCallback((userId: string) => onChange(xor(selectedIds, [userId])), [selectedIds, onChange]);

  const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };

  const renderSurgeonTableCell = useMemo(() => {
    return getRenderSurgeonTableCell(regionId, insertedIds, handleSelect);
  }, [regionId, insertedIds, handleSelect]);

  return (
    <>
      <SearchInput
        placeholder="Search"
        value={searchValue}
        onChange={onSearchChange}
        className="mb-6 sticky top-0 z-10"
      />
      <DataTable
        type="default"
        label="Surgeons"
        hidePadding
        hidePagination
        columns={SURGEON_COLUMNS}
        renderCell={renderSurgeonTableCell}
        data={dataList || []}
        // FIXME: Redundant props
        isLoading={false}
        isFetching={false}
        totalItems={dataList.length}
        itemsPerPage={dataList.length}
        totalPages={1}
        page={1}
        onPageChange={noopFn}
        onItemsPerPageChange={noopFn}
        onSortByChange={noopFn}
      />
    </>
  );
};
